import { useEffect, useState } from 'react';
import { ShieldOff, Loader2, Unlock, ShieldCheck } from 'lucide-react';
import {
  fetchLockedOutDrivers,
  clearPaymentLockout,
  type LockedOutDriverRow,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './LockedOutDriversPage.module.css';

/**
 * Admin: Locked-Out Drivers.
 *
 * Lists drivers currently restricted to wallet-only charging because
 * their card path tripped the consecutive-failure threshold (see
 * commit 7b). Clear-lockout button lifts the restriction immediately
 * — the 30-day timer does NOT resume; the driver starts fresh.
 *
 * The recent-failure-codes column is intentionally absent. The
 * AuditLog collection lacks a `{tenant_id, user_id, action,
 * created_at}` index, so a per-driver lookup of the last 3
 * `payment.failure_counter.incremented` rows would scan an
 * unbounded action-slice as the platform accumulates lockouts.
 * The follow-up commit will add that index AND the column
 * together; we don't denormalize onto User in the meantime. Ops
 * who need to see why a driver tripped can query the audit log
 * directly by user id.
 */
export function LockedOutDriversPage() {
  useRecentRoute('Locked-out drivers');
  const [rows, setRows] = useState<LockedOutDriverRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<LockedOutDriverRow | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchLockedOutDrivers();
      setRows(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load locked-out drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const handleConfirmClear = async (): Promise<void> => {
    if (!confirmTarget) return;
    setConfirmPending(true);
    try {
      const targetId = confirmTarget.user_id;
      await clearPaymentLockout(targetId);
      // Optimistic row removal — list endpoint filters on lockout >
      // now() so a refetch would drop the row anyway, but this avoids
      // the round-trip flicker.
      setRows((prev) => prev.filter((r) => r.user_id !== targetId));
      setConfirmTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear lockout');
    } finally {
      setConfirmPending(false);
    }
  };

  const driverLabel = (r: LockedOutDriverRow): string =>
    r.name ?? r.username ?? r.email;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}><ShieldOff size={20} /></span>
          <div>
            <h1 className={styles.title}>Locked-out drivers</h1>
            <p className={styles.subtitle}>
              Drivers restricted to wallet-only charging after consecutive card failures.
              Auto-clears after 30 days, or clear manually below.
            </p>
          </div>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      {loading && rows.length === 0 ? (
        <div className={styles.empty}>
          <Loader2 size={18} className={styles.spin} />
          <span>Loading locked-out drivers…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><ShieldCheck size={28} /></span>
          <p className={styles.emptyTitle}>No drivers currently locked out</p>
          <p className={styles.emptyHint}>
            When a driver's card fails repeatedly, they'll appear here until the
            lockout clears.
          </p>
        </div>
      ) : (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Driver</th>
                <th>Locked since</th>
                <th>Days remaining</th>
                <th className={styles.colNum}>Failures</th>
                <th className={styles.colActions} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className={styles.row}>
                  <td>
                    <div className={styles.driverName}>{driverLabel(r)}</div>
                    {(r.name || r.username) && r.email !== driverLabel(r) && (
                      <div className={styles.driverEmail}>{r.email}</div>
                    )}
                  </td>
                  <td className={styles.timeCell}>
                    {formatRelativePast(r.locked_since)}
                  </td>
                  <td className={styles.timeCell}>
                    {formatRemaining(r.days_remaining, r.hours_remaining)}
                  </td>
                  <td className={styles.colNum}>
                    <span className={styles.failureBadge}>{r.failed_charge_count}</span>
                  </td>
                  <td className={styles.colActions}>
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={() => setConfirmTarget(r)}
                    >
                      <Unlock size={12} />
                      Clear lockout
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={Boolean(confirmTarget)}
        onClose={() => setConfirmTarget(null)}
        title={confirmTarget ? `Clear lockout for ${driverLabel(confirmTarget)}?` : ''}
        size="sm"
      >
        {confirmTarget && (
          <div className={styles.confirmBody}>
            <p>
              They&rsquo;ll be able to attempt card payments again immediately.
              The 30-day timer will not resume.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setConfirmTarget(null)}
                disabled={confirmPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDanger}
                onClick={() => void handleConfirmClear()}
                disabled={confirmPending}
              >
                {confirmPending
                  ? <><Loader2 size={12} className={styles.spin} /> Clearing…</>
                  : 'Clear lockout'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Time helpers ─────────────────────────────────────────────────────
// Inline rather than a shared util — date-fns isn't in @evi/admin's
// deps and the formatters are trivial. If admin grows more relative-
// time surfaces, lift these into lib/.

function formatRelativePast(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return 'just now';
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function formatRemaining(days: number, hours: number): string {
  if (days >= 1) return `${days} ${days === 1 ? 'day' : 'days'}`;
  if (hours >= 1) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return 'less than 1 hour';
}
